import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { DEFAULT_VALUES } from "@/libraries/web-framework";

import {
    PricingPeriod,
    SpecialPlanNames,
    SpecialProductKeys
} from "../constants";
import { usePriceFinderStore } from "../store/store";

import { Annual } from "./annual";
import { centerJustifiedStyling } from "./card-container";
import { Free } from "./free";
import { Monthly } from "./monthly";

const bodyTextStyles = css([
    centerJustifiedStyling,
    {
        "padding": "32px 0px",
        "@media(min-width: 1024px)": {
            padding: "0px 0px"
        }
    }
]);

type PriceCardBodyProps = {
    billingCycle: PricingPeriod;
    fontSize: string;
    overSignupLimit: boolean;
    planName: string;
    currencyISOCode: string;
    unitCount: number;
    enterpriseMinimumCount: number;
    productKey: string;
    priceCalcIdx: number;
    contactURL: string;
};

const PriceCardBody = ({
    billingCycle,
    fontSize,
    overSignupLimit,
    planName,
    currencyISOCode,
    unitCount,
    enterpriseMinimumCount,
    productKey,
    priceCalcIdx,
    contactURL
}: PriceCardBodyProps) => {
    const [{ priceCalcs, messages }, actions] = usePriceFinderStore();
    const priceCalc = priceCalcs[priceCalcIdx];

    if (!priceCalc) {
        throw new Error(
            `Price calculation was not found for index ${priceCalcIdx}`
        );
    }

    const { formattedTotalPrice } = priceCalc;

    let planMessage =
        billingCycle === "MONTHLY" ? (
            <Monthly
                priceCalc={priceCalc}
                planName={planName}
                fontSize={fontSize}
            />
        ) : (
            <Annual
                priceCalc={priceCalc}
                planName={planName}
                fontSize={fontSize}
            />
        );

    if (overSignupLimit && productKey === SpecialProductKeys.BITBUCKET) {
        planMessage = (
            <div css={bodyTextStyles}>
                <FormattedMessage
                    id="Pricing.card.body.bitbucket.overSignupLimit"
                    defaultMessage="For teams of more than 35,000, <a>contact our sales team.</a>"
                    values={{ a: (str) => <a href={contactURL}>{str}</a> }}
                />
            </div>
        );
    } else if (overSignupLimit) {
        planMessage = (
            <div css={bodyTextStyles}>
                <FormattedMessage {...messages.cardOverSignupLimit} />
            </div>
        );
    }

    switch (planName) {
        case SpecialPlanNames.FREE:
            planMessage = (
                <Free
                    planName={planName}
                    formattedZeroPrice={formattedTotalPrice}
                    fontSize={fontSize}
                />
            );
            break;

        case SpecialPlanNames.ENTERPRISE:
            if (billingCycle === "MONTHLY") {
                planMessage = (
                    <div css={bodyTextStyles}>
                        <span>
                            <BilledAnnuallyButton
                                onClick={() =>
                                    actions.updateBillingCycle("ANNUAL")
                                }
                            />{" "}
                            <FormattedMessage
                                id="Pricing.card.body.enterprise.monthly"
                                defaultMessage="Switch the Billing cycle to Annual to view Enterprise pricing."
                            />
                        </span>
                    </div>
                );
            } else if (currencyISOCode === "JPY") {
                planMessage = (
                    <div css={bodyTextStyles}>
                        <FormattedMessage
                            id="Pricing.card.body.enterprise.jpy"
                            defaultMessage="For pricing details see FAQ below or talk to our sales team"
                        />
                    </div>
                );
            } else if (overSignupLimit) {
                planMessage = (
                    <div css={bodyTextStyles}>
                        <FormattedMessage
                            id="Pricing.card.body.enterprise.overSignupLimit"
                            defaultMessage="Talk to our sales team about pricing for Cloud Enterprise"
                        />
                    </div>
                );
            } else if (unitCount < enterpriseMinimumCount) {
                planMessage = (
                    <div css={bodyTextStyles}>
                        <FormattedMessage
                            {...messages.cardEnterpriseMinimumCount}
                            values={{
                                ...DEFAULT_VALUES,
                                enterpriseMinimumCount
                            }}
                        />
                    </div>
                );
            }
            break;
    }
    return planMessage;
};

export { PriceCardBody };

interface BilledAnnuallyButtonProps {
    onClick: () => void;
}

const BilledAnnuallyButton = (props: BilledAnnuallyButtonProps) => {
    return (
        <button
            css={[
                css({
                    "background": "transparent",
                    "border": 0,
                    "cursor": "pointer",
                    "color": "#0065ff",
                    "fontFamily": "inherit",
                    "fontSize": "1rem",
                    "lineHeight": "32px",
                    "margin": "0 2px",
                    "padding": 0,
                    "textOverflow": "ellipsis",
                    "textDecoration": "underline",
                    "whiteSpace": "nowrap",
                    ":hover": {
                        color: "#0049b0"
                    }
                })
            ]}
            type="button"
            onClick={props.onClick}
            tabIndex={0}
            data-event-component="linkButton"
            data-event="clicked"
        >
            <FormattedMessage
                id="Pricing.card.body.enterprise.billedAnnually"
                defaultMessage="Billed annually."
            />
        </button>
    );
};
